<template>
  <div class="betriebsordner">
    <div class="row align-items-center header-row">
      <div class="col-12 col-md-auto">
        <h5 class="title">Betriebsordner</h5>
      </div>
      <div class="col-12 col-md">
        <fg-input
          v-model="searchQuery"
          placeholder="Dokumente durchsuchen..."
          addon-left-icon="bi bi-search"
          class="mb-0"
        ></fg-input>
      </div>
      <div class="col-12 col-md-auto header-actions" v-if="role > 1">
        <p-button type="default" @click="newFolder()" class="mt-0 mr-2">
          <i class="bi bi-folder-plus"></i> Ordner
        </p-button>
        <p-button type="success" @click="newDoc()" class="mt-0">
          <i class="bi bi-file-earmark-plus"></i> Hinzufügen
        </p-button>
      </div>
    </div>

    <!-- Search results view -->
    <div v-if="searchQuery && searchQuery.trim().length > 0" class="mt-3">
      <h6 class="mt-3 mb-3">
        Suchergebnisse für "{{ searchQuery }}"
        <small class="text-muted">({{ searchResults.length }})</small>
      </h6>
      <div class="row" v-if="searchResults.length > 0">
        <div
          class="col-lg-4 col-md-6"
          v-for="document in searchResults"
          :key="document.key"
        >
          <div class="document-card card mt-3">
            <div class="card-body">
              <div class="doc-icon-wrap">
                <i :class="documentIcon(document)"></i>
              </div>
              <h6 class="card-title">{{ document.title }}</h6>
              <p class="card-subtitle text-muted small mb-2">
                <i class="bi bi-folder2"></i> {{ document.category }}
              </p>
              <p-button
                type="success"
                size="sm"
                @click="loadVideo(document.title, document.url)"
                v-if="document.contentType == 'video'"
              ><i class="bi bi-play-circle"></i> Video</p-button>
              <p-button
                type="success"
                size="sm"
                @click="loadDoc(document.filename)"
                v-if="document.contentType == 'pdf'"
              ><i class="bi bi-file-pdf"></i> Dokument</p-button>
              <p-button
                type="success"
                size="sm"
                @click="loadImage(document.title, document.url)"
                v-if="document.contentType == 'image'"
              ><i class="bi bi-image"></i> Bild</p-button>
            </div>
            <div class="card-footer text-right" v-if="role > 1">
              <p-button
                type="default"
                @click="editDoc(document.key, document.title, document.filename, document.category, document.locationID)"
                size="sm"
                title="Element bearbeiten"
                class="mr-2"
              ><i class="bi bi-pencil-square"></i></p-button>
              <p-button
                type="danger"
                @click="removeDoc(document.key, document.title)"
                size="sm"
                title="Element löschen"
              ><i class="bi bi-trash"></i></p-button>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center text-muted py-4">
        <i class="bi bi-search" style="font-size: 2rem"></i>
        <p class="mt-2">Keine Dokumente gefunden.</p>
      </div>
    </div>

    <!-- Folder view -->
    <div class="mt-3" v-else>
      <div v-if="folderNames.length > 0">
        <!-- Mobile folder selector -->
        <div class="folder-select-mobile d-md-none">
          <el-select
            v-model="activeFolder"
            placeholder="Ordner wählen"
            class="w-100"
          >
            <el-option
              v-for="folder in folderNames"
              :key="folder"
              :value="folder"
              :label="folder"
            ></el-option>
          </el-select>
          <div class="folder-actions-mobile mt-2" v-if="role > 1 && activeFolder">
            <p-button
              type="default"
              size="sm"
              @click="renameFolder(activeFolder)"
              class="mr-2"
            ><i class="bi bi-pencil-square"></i> Umbenennen</p-button>
            <p-button
              type="danger"
              size="sm"
              @click="removeFolder(activeFolder)"
            ><i class="bi bi-trash"></i> Löschen</p-button>
          </div>
        </div>

        <!-- Desktop folder pill bar -->
        <div class="folder-pillbar d-none d-md-flex">
          <button
            v-for="folder in folderNames"
            :key="folder"
            type="button"
            class="folder-pill"
            :class="{ active: activeFolder === folder }"
            @click="activeFolder = folder"
          >
            <i class="bi bi-folder2-open"></i>
            <span class="folder-pill-label">{{ folder }}</span>
            <span
              v-if="role > 1"
              class="folder-actions ml-2"
              @click.stop
            >
              <i
                class="bi bi-pencil-square folder-action"
                title="Ordner umbenennen"
                @click.stop="renameFolder(folder)"
              ></i>
              <i
                class="bi bi-trash folder-action ml-1"
                title="Ordner löschen"
                @click.stop="removeFolder(folder)"
              ></i>
            </span>
          </button>
        </div>

        <!-- Folder content -->
        <div v-if="activeFolder">
          <div class="row mt-3" v-if="documentsByFolder[activeFolder] && documentsByFolder[activeFolder].length > 0">
            <div
              class="col-lg-4 col-md-6"
              v-for="document in documentsByFolder[activeFolder]"
              :key="document.key"
            >
              <div class="document-card card mt-3">
                <div class="card-body">
                  <div class="doc-icon-wrap">
                    <i :class="documentIcon(document)"></i>
                  </div>
                  <h6 class="card-title">{{ document.title }}</h6>
                  <p-button
                    type="success"
                    size="sm"
                    @click="loadVideo(document.title, document.url)"
                    v-if="document.contentType == 'video'"
                  ><i class="bi bi-play-circle"></i> Video anzeigen</p-button>
                  <p-button
                    type="success"
                    size="sm"
                    @click="loadDoc(document.filename)"
                    v-if="document.contentType == 'pdf'"
                  ><i class="bi bi-file-pdf"></i> Dokument anzeigen</p-button>
                  <p-button
                    type="success"
                    size="sm"
                    @click="loadImage(document.title, document.url)"
                    v-if="document.contentType == 'image'"
                  ><i class="bi bi-image"></i> Bild anzeigen</p-button>
                </div>
                <div class="card-footer text-right" v-if="role > 1">
                  <p-button
                    type="default"
                    @click="editDoc(document.key, document.title, document.filename, document.category, document.locationID)"
                    size="sm"
                    title="Element bearbeiten"
                    class="mr-2"
                  ><i class="bi bi-pencil-square"></i></p-button>
                  <p-button
                    type="danger"
                    @click="removeDoc(document.key, document.title)"
                    size="sm"
                    title="Element löschen"
                  ><i class="bi bi-trash"></i></p-button>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-center text-muted py-4">
            <i class="bi bi-folder2" style="font-size: 2rem"></i>
            <p class="mt-2">Dieser Ordner ist leer.</p>
          </div>
        </div>
      </div>
      <div v-else class="text-center text-muted py-5">
        <i class="bi bi-folder-plus" style="font-size: 2.5rem"></i>
        <p class="mt-3">Noch keine Ordner vorhanden.</p>
        <p-button v-if="role > 1" type="default" @click="newFolder()">
          <i class="bi bi-folder-plus"></i> Ordner anlegen
        </p-button>
      </div>
    </div>

    <!-- Document edit modal -->
    <modal
      :show.sync="showEditForm"
      headerClasses="justify-content-center"
      modalClasses="modal-betriebsordner"
      modalMaxWidth="700px"
    >
      <h5 slot="header" class="title mb-0" v-if="editMode == 'Edit'">Element bearbeiten</h5>
      <h5 slot="header" class="title mb-0" v-if="editMode == 'New'">Element hinzufügen</h5>
      <fg-input placeholder="Titel des Dokuments/Videos" label="Titel"
        v-model="editDocTitle"></fg-input>
      <label class="pt-1">Ordner</label>
      <el-select
          placeholder="Ordner"
          v-model="editDocCategory"
          filterable>
        <el-option
          v-for="folder in folderNames"
          :key="folder"
          :value="folder"
          :label="folder">
        </el-option>
      </el-select>
      <label class="pt-2" v-if="multiLocationMode == 1">Standorte</label>
      <el-select multiple
          placeholder="Standorte"
          v-model="editDocLocation"
          v-if="multiLocationMode == 1">
        <el-option v-for="location in locations"
                  :key="location.key"
                  :label="location.name"
                  :value="location.key"
                ></el-option>
      </el-select>
      <div class="pt-3">
        <p-button type="default" @click="chooseFile" v-if="!editDocFiledata">Datei hochladen (PDF, Video oder Bild)</p-button>
        <p class="pt-3" v-if="editDocFiledata">Datei: {{ editDocFiledata.name }}</p>
        <input type="file" @change="prepareFile" ref="fileUpload" accept="application/pdf,video/*,image/*" style="display: none">
      </div>
      <div class="py-3" v-if="uploading">
        <el-progress :percentage="uploadProgress" :stroke-width="10" color="#15a34a"></el-progress>
      </div>
      <template slot="footer">
        <div class="left-side">
          <p-button type="default" link @click="showEditForm = false;"
            >Abbrechen</p-button
          >
        </div>
        <div class="divider"></div>
        <div class="right-side">
          <p-button type="success" link @click="updateDoc" v-if="editMode == 'Edit'"
            >Speichern</p-button
          >
          <p-button type="success" link @click="addDoc" v-if="editMode == 'New'"
            >Hinzufügen</p-button
          >
        </div>
      </template>
    </modal>

    <!-- Folder edit modal -->
    <modal
      :show.sync="showFolderForm"
      headerClasses="justify-content-center"
      modalClasses="modal-betriebsordner"
      modalMaxWidth="500px"
    >
      <h5 slot="header" class="title mb-0" v-if="folderMode == 'New'">Neuer Ordner</h5>
      <h5 slot="header" class="title mb-0" v-if="folderMode == 'Rename'">Ordner umbenennen</h5>
      <fg-input
        placeholder="Name des Ordners"
        label="Name"
        v-model="folderName"
      ></fg-input>
      <label class="pt-2" v-if="multiLocationMode == 1">Standorte</label>
      <el-select multiple
          placeholder="Standorte"
          v-model="folderLocation"
          v-if="multiLocationMode == 1 && folderMode == 'New'">
        <el-option v-for="location in locations"
                  :key="location.key"
                  :label="location.name"
                  :value="location.key"
                ></el-option>
      </el-select>
      <template slot="footer">
        <div class="left-side">
          <p-button type="default" link @click="showFolderForm = false;">Abbrechen</p-button>
        </div>
        <div class="divider"></div>
        <div class="right-side">
          <p-button type="success" link @click="saveFolder" v-if="folderMode == 'New'">Anlegen</p-button>
          <p-button type="success" link @click="saveFolder" v-if="folderMode == 'Rename'">Speichern</p-button>
        </div>
      </template>
    </modal>

    <modal
      :show.sync="showVideoPlayer"
      headerClasses="justify-content-center"
      modalClasses="modal-betriebsordner"
      modalMaxWidth="700px"
    >
      <h5 slot="header" class="title mb-0">{{videoTitle}}</h5>
      <video width="100%" controls crossorigin="anonymous" preload="none" ref="videoRef" autoplay>
        <source src="" type="video/*">
        Dein Browser unterstützt leider keine Video-Wiedergabe.
      </video>
    </modal>

    <modal
      :show.sync="showImageViewer"
      headerClasses="justify-content-center"
      modalClasses="modal-betriebsordner"
      modalMaxWidth="900px"
    >
      <h5 slot="header" class="title mb-0">{{ imageTitle }}</h5>
      <div class="text-center">
        <img v-if="imageUrl" :src="imageUrl" :alt="imageTitle" class="image-preview" />
      </div>
      <template slot="footer">
        <div class="left-side">
          <p-button type="default" link @click="showImageViewer = false">Schließen</p-button>
        </div>
        <div class="divider"></div>
        <div class="right-side">
          <a :href="imageUrl" target="_blank" rel="noopener noreferrer" class="btn btn-success btn-link">In neuem Tab öffnen</a>
        </div>
      </template>
    </modal>
  </div>
</template>
<script>
import Vue from "vue";
import { Modal } from "src/components/UIComponents";
import { Loading, MessageBox, Select, Option, Progress } from "element-ui";
import router from "../../../../main";
import store from "../../../../store";
import locations from "../../../../locationStore";
import { db, fc, FirebaseStorage } from "../../../../firebaseDb";
Vue.use(Loading);

export default {
  components: {
    MessageBox,
    Modal,
    [Progress.name]: Progress,
    [Select.name]: Select,
    [Option.name]: Option,
  },

  data() {
    return {
      allDocuments: [],
      folders: [],
      loading: true,
      location: store.getters.userlocation,
      role: store.getters.userrole,
      searchQuery: "",
      activeFolder: null,
      showEditForm: false,
      showVideoPlayer: false,
      showImageViewer: false,
      imageTitle: '',
      imageUrl: '',
      editDocId: null,
      editDocTitle: null,
      editDocCategory: null,
      editDocFilename: null,
      editDocLocation: [],
      editDocFiledata: null,
      editDocContentType: null,
      uploadValue: 0,
      editMode: '',
      videoTitle: '',
      videoUrl: '',
      uploading: false,
      uploadProgress: 0,
      multiLocationMode: process.env.VUE_APP_MULTIPLE_LOCATIONS,
      locations: locations.getters.locations.locations,
      // Folder management
      showFolderForm: false,
      folderMode: '',
      folderEditId: null,
      folderEditOldName: null,
      folderName: '',
      folderLocation: [],
      foldersUnsubscribe: null,
      docsUnsubscribe: null,
    };
  },
  computed: {
    folderNames() {
      // Union of explicitly defined folders + categories that already exist on documents (resilience).
      const set = new Set();
      this.folders.forEach((f) => set.add(f.name));
      this.allDocuments.forEach((d) => {
        if (d.category) set.add(d.category);
      });
      return Array.from(set).sort((a, b) => a.localeCompare(b, "de"));
    },
    documentsByFolder() {
      const map = {};
      this.folderNames.forEach((n) => (map[n] = []));
      this.allDocuments.forEach((d) => {
        const cat = d.category || "Allgemein";
        if (!map[cat]) map[cat] = [];
        map[cat].push(d);
      });
      // Sort within each folder by title
      Object.keys(map).forEach((k) => {
        map[k].sort((a, b) => (a.title || "").localeCompare(b.title || "", "de"));
      });
      return map;
    },
    searchResults() {
      const q = (this.searchQuery || "").trim().toLowerCase();
      if (!q) return [];
      return this.allDocuments
        .filter((d) => {
          return (
            (d.title && d.title.toLowerCase().includes(q)) ||
            (d.category && d.category.toLowerCase().includes(q)) ||
            (d.filename && d.filename.toLowerCase().includes(q))
          );
        })
        .sort((a, b) => (a.title || "").localeCompare(b.title || "", "de"));
    },
  },
  watch: {
    folderNames: {
      immediate: true,
      handler(names) {
        if (!names || names.length === 0) {
          this.activeFolder = null;
          return;
        }
        // Restore previously selected folder for this location (if still available)
        const saved = this.getSavedActiveFolder();
        if (saved && names.indexOf(saved) !== -1) {
          if (this.activeFolder !== saved) {
            this.activeFolder = saved;
          }
          return;
        }
        if (!this.activeFolder || names.indexOf(this.activeFolder) === -1) {
          this.activeFolder = names[0];
        }
      },
    },
    activeFolder(name) {
      if (name) {
        this.saveActiveFolder(name);
      }
    },
  },
  created() {
    this.loadData();
    if (this.multiLocationMode == 0) {
      this.editDocLocation = [this.location];
      this.folderLocation = [this.location];
    }
    store.watch(
      (state) => state.userlocation,
      () => {
        this.location = store.getters.userlocation;
        this.loadData();
      }
    );
  },
  beforeDestroy() {
    if (this.foldersUnsubscribe) this.foldersUnsubscribe();
    if (this.docsUnsubscribe) this.docsUnsubscribe();
  },
  methods: {
    activeFolderStorageKey() {
      return 'betriebsordner.activeFolder.' + (this.location || 'default');
    },
    getSavedActiveFolder() {
      try {
        return sessionStorage.getItem(this.activeFolderStorageKey());
      } catch (e) {
        return null;
      }
    },
    saveActiveFolder(name) {
      try {
        sessionStorage.setItem(this.activeFolderStorageKey(), name);
      } catch (e) {
        // ignore storage errors (private mode, quota, ...)
      }
    },
    documentIcon(document) {
      if (document.contentType === "video") {
        return "bi bi-camera-video doc-icon doc-icon-video";
      }
      if (document.contentType === "image") {
        return "bi bi-file-earmark-image doc-icon doc-icon-image";
      }
      return "bi bi-file-earmark-pdf doc-icon doc-icon-pdf";
    },
    loadDoc(doc) {
      router.push({ name: "Print", params: { file: doc } });
    },
    loadVideo(title, url) {
      this.videoTitle = title;
      this.videoUrl = url;
      this.$refs.videoRef.src = url;
      this.showVideoPlayer = true;
    },
    loadImage(title, url) {
      this.imageTitle = title;
      this.imageUrl = url;
      this.showImageViewer = true;
    },
    fileTypeInfo(file) {
      if (file.type.includes("video")) {
        return { contentType: "video", folder: "Video" };
      }
      if (file.type.startsWith("image/")) {
        return { contentType: "image", folder: "Bilder" };
      }
      return { contentType: "pdf", folder: "PDF" };
    },
    loadData() {
      // Unsubscribe from previous listeners (when location changes)
      if (this.foldersUnsubscribe) this.foldersUnsubscribe();
      if (this.docsUnsubscribe) this.docsUnsubscribe();

      const betriebsordnerRef = db.collection("betriebsordner");
      this.docsUnsubscribe = betriebsordnerRef
        .where('locationID', 'array-contains', this.location)
        .orderBy("title")
        .onSnapshot((snapshotChange) => {
          const docs = [];
          snapshotChange.forEach((doc) => {
            docs.push({
              key: doc.id,
              title: doc.data().title,
              category: doc.data().category || "Allgemein",
              filename: doc.data().filename,
              url: doc.data().url,
              locationID: doc.data().locationID,
              contentType: doc.data().contentType || "pdf",
            });
          });
          this.allDocuments = docs;
          this.loading = false;
        });

      const foldersRef = db.collection("betriebsordnerFolders");
      this.foldersUnsubscribe = foldersRef
        .where('locationID', 'array-contains', this.location)
        .onSnapshot((snap) => {
          const list = [];
          snap.forEach((doc) => {
            list.push({
              key: doc.id,
              name: doc.data().name,
              locationID: doc.data().locationID,
            });
          });
          this.folders = list;
        }, (err) => {
          // Silently ignore; folders collection may not exist yet
          console.log("Folder load error:", err.message);
        });
    },

    // ---- Folder management ----
    newFolder() {
      this.folderMode = 'New';
      this.folderEditId = null;
      this.folderEditOldName = null;
      this.folderName = '';
      if (this.multiLocationMode == 0) {
        this.folderLocation = [this.location];
      } else {
        this.folderLocation = [this.location];
      }
      this.showFolderForm = true;
    },
    renameFolder(name) {
      this.folderMode = 'Rename';
      this.folderEditOldName = name;
      this.folderName = name;
      // Find the folder doc (may not exist for legacy categories)
      const f = this.folders.find((x) => x.name === name);
      this.folderEditId = f ? f.key : null;
      this.folderLocation = f ? f.locationID : [this.location];
      this.showFolderForm = true;
    },
    saveFolder() {
      const name = (this.folderName || '').trim();
      if (!name) {
        return;
      }
      if (this.folderMode === 'New') {
        // Prevent duplicates
        if (this.folderNames.indexOf(name) !== -1) {
          MessageBox.alert('Ein Ordner mit diesem Namen existiert bereits.', 'Hinweis', { type: 'warning' });
          return;
        }
        const locs = this.folderLocation && this.folderLocation.length > 0
          ? this.folderLocation
          : [this.location];
        db.collection("betriebsordnerFolders").add({
          name: name,
          locationID: locs,
        }).then(() => {
          this.showFolderForm = false;
        }).catch((err) => {
          console.log("Error adding folder: " + err.message);
        });
      } else if (this.folderMode === 'Rename') {
        if (name === this.folderEditOldName) {
          this.showFolderForm = false;
          return;
        }
        if (this.folderNames.indexOf(name) !== -1) {
          MessageBox.alert('Ein Ordner mit diesem Namen existiert bereits.', 'Hinweis', { type: 'warning' });
          return;
        }
        const oldName = this.folderEditOldName;
        const folderId = this.folderEditId;
        // Update folder doc (if exists) and all documents using the old category
        const tasks = [];
        if (folderId) {
          tasks.push(db.collection("betriebsordnerFolders").doc(folderId).update({ name: name }));
        } else {
          // Create folder doc with new name (legacy category had no folder doc)
          tasks.push(db.collection("betriebsordnerFolders").add({
            name: name,
            locationID: [this.location],
          }));
        }
        const affected = this.allDocuments.filter((d) => d.category === oldName);
        affected.forEach((d) => {
          tasks.push(
            db.collection("betriebsordner").doc(d.key).update({ category: name })
          );
        });
        Promise.all(tasks).then(() => {
          this.showFolderForm = false;
        }).catch((err) => {
          console.log("Error renaming folder: " + err.message);
        });
      }
    },
    removeFolder(name) {
      const docsInFolder = this.allDocuments.filter((d) => d.category === name);
      if (docsInFolder.length > 0) {
        MessageBox.alert(
          'Der Ordner "' + name + '" enthält noch ' + docsInFolder.length +
            ' Dokument(e). Bitte verschiebe oder lösche zuerst alle Dokumente.',
          'Ordner nicht leer',
          { type: 'warning' }
        );
        return;
      }
      MessageBox.confirm(
        'Soll der Ordner "' + name + '" wirklich gelöscht werden?',
        'Warnung',
        {
          confirmButtonText: 'Löschen',
          cancelButtonText: 'Abbrechen',
          type: 'warning',
        }
      ).then(() => {
        const f = this.folders.find((x) => x.name === name);
        if (f) {
          db.collection("betriebsordnerFolders").doc(f.key).delete();
        }
        // If folder existed only as legacy category with no doc, there's nothing to delete in folders collection
      }).catch(() => {});
    },

    // ---- Document management ----
    newDoc() {
      this.editDocId = null;
      this.editDocTitle = null;
      this.editDocFilename = null;
      this.editDocLocation = this.multiLocationMode == 0 ? [this.location] : null;
      this.editDocCategory = this.folderNames.length > 0 ? this.folderNames[0] : null;
      this.editDocFiledata = null;
      this.editDocContentType = null;
      this.uploadProgress = 0;
      this.editMode = "New";
      this.showEditForm = true;
    },
    addDoc() {
      let folder = null;
      let filename = null;
      if (this.editDocFiledata && this.editDocTitle && this.editDocLocation && this.editDocCategory) {
        filename = this.editDocFiledata.name;
        const info = this.fileTypeInfo(this.editDocFiledata);
        this.editDocContentType = info.contentType;
        folder = info.folder;
          this.uploading = true;
          const storageRef = FirebaseStorage.ref("/" + folder + "/" + filename).put(this.editDocFiledata);
          storageRef.on('state_changed', snapshot => {
            this.uploadProgress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          }, error => { console.log(error.message) },
            () => {
              this.uploadProgress = 100;
              storageRef.snapshot.ref.getDownloadURL().then((url) => {
                if (this.editDocContentType == "pdf") {
                  filename = this.editDocFiledata.name.replace(".pdf", "");
                }
                db.collection("betriebsordner")
                  .add({
                    title: this.editDocTitle,
                    category: this.editDocCategory,
                    filename: filename,
                    locationID: this.editDocLocation,
                    url: url,
                    contentType: this.editDocContentType,
                  })
                  .then(() => {
                    this.editDocId = null;
                    this.editDocTitle = null;
                    this.editDocFilename = null;
                    this.editDocLocation = null;
                    this.editDocCategory = null;
                    this.editDocFiledata = null;
                    this.editDocContentType = null;
                    this.uploading = false;
                    this.showEditForm = false;
                    this.editMode = "";
                  })
                  .catch((error) => {
                    console.log("Error adding document with attachment " + error);
                  });
              });
            }
          );
      } else {
        console.log('Missing data')
      }
    },
    editDoc(id, title, filename, category, location) {
      this.editDocId = id;
      this.editDocTitle = title;
      this.editDocFilename = filename;
      this.editDocLocation = location;
      this.editDocCategory = category;
      this.editDocFiledata = null;
      this.uploadProgress = 0;
      this.editMode = "Edit";
      this.showEditForm = true;
    },
    updateDoc() {
      if (!(this.editDocTitle && this.editDocLocation && this.editDocCategory)) {
        console.log('Missing data');
        return;
      }
      // If no new file uploaded, only update metadata
      if (!this.editDocFiledata) {
        db.collection("betriebsordner").doc(this.editDocId)
          .update({
            title: this.editDocTitle,
            category: this.editDocCategory,
            locationID: this.editDocLocation,
          })
          .then(() => {
            this.editDocId = null;
            this.editDocTitle = null;
            this.editDocFilename = null;
            this.editDocLocation = null;
            this.editDocCategory = null;
            this.editDocFiledata = null;
            this.editDocContentType = null;
            this.showEditForm = false;
            this.editMode = "";
          })
          .catch((error) => {
            console.log("Error updating document " + error);
          });
        return;
      }
      let folder = null;
      let filename = this.editDocFiledata.name;
      const info = this.fileTypeInfo(this.editDocFiledata);
      this.editDocContentType = info.contentType;
      folder = info.folder;
      this.uploading = true;
      const storageRef = FirebaseStorage.ref("/" + folder + "/" + filename).put(this.editDocFiledata);
      storageRef.on('state_changed', snapshot => {
        this.uploadProgress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
      }, error => { console.log(error.message) },
        () => {
          this.uploadProgress = 100;
          storageRef.snapshot.ref.getDownloadURL().then((url) => {
            if (this.editDocContentType == "pdf") {
              filename = this.editDocFiledata.name.replace(".pdf", "");
            }
            db.collection("betriebsordner").doc(this.editDocId)
              .update({
                title: this.editDocTitle,
                category: this.editDocCategory,
                filename: filename,
                locationID: this.editDocLocation,
                url: url,
                contentType: this.editDocContentType,
              })
              .then(() => {
                this.editDocId = null;
                this.editDocTitle = null;
                this.editDocFilename = null;
                this.editDocLocation = null;
                this.editDocCategory = null;
                this.editDocFiledata = null;
                this.editDocContentType = null;
                this.uploading = false;
                this.showEditForm = false;
                this.editMode = "";
              })
              .catch((error) => {
                console.log("Error updating document with attachment " + error);
              });
          });
        }
      );
    },
    removeDoc(id, title) {
      MessageBox.confirm(
        'Soll das Element "' + title + '" wirklich aus dem Betriebsordner gelöscht werden?',
        "Warnung",
        {
          confirmButtonText: "Löschen",
          cancelButtonText: "Abbrechen",
          type: "warning",
          callback: (action) => {
            if (action == "confirm") {
              db.collection("betriebsordner")
                .doc(id)
                .delete();
            }
          },
        }
      );
    },
    chooseFile() {
      this.$refs.fileUpload.click()
    },
    prepareFile(event) {
      this.uploadValue = 0;
      this.editDocFiledata = event.target.files[0];
    },
  },
};
</script>
<style scoped>
.betriebsordner .header-row {
  row-gap: 0.5rem;
}
.header-actions {
  text-align: right;
}
@media (max-width: 767.98px) {
  .header-actions {
    text-align: left;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .header-actions > .btn,
  .header-actions > .p-button {
    margin-right: 0 !important;
    padding: 0.4rem 0.75rem;
    font-size: 0.85rem;
  }
  /* Title smaller on mobile */
  .betriebsordner .title {
    margin-bottom: 0.25rem;
  }
}

.folder-pillbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
.folder-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 999px;
  padding: 0.4rem 0.9rem;
  font-size: 0.9rem;
  line-height: 1.2;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.folder-pill:hover {
  border-color: #15a34a;
  color: #15a34a;
}
.folder-pill.active {
  background: #15a34a;
  border-color: #15a34a;
  color: #fff;
}
.folder-pill.active .folder-action {
  color: #fff;
}
.folder-pill-label {
  white-space: nowrap;
}

.folder-select-mobile {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
.folder-select-mobile :deep(.el-select) {
  width: 100%;
}
.folder-select-mobile .el-select {
  width: 100%;
}
.folder-actions-mobile {
  display: flex;
  gap: 0.5rem;
}
.folder-actions-mobile > .btn,
.folder-actions-mobile > .p-button {
  flex: 1 1 auto;
  margin-right: 0 !important;
}

.document-card {
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  height: calc(100% - 1rem);
  display: flex;
  flex-direction: column;
}
.document-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
}
.document-card .card-body {
  flex: 1 1 auto;
}
.document-card .card-title {
  font-weight: 600;
  margin-bottom: 0.5rem;
  word-break: break-word;
}
.doc-icon-wrap {
  margin-bottom: 0.75rem;
}
.doc-icon {
  font-size: 2rem;
}
.doc-icon-pdf {
  color: #dc3545;
}
.doc-icon-video {
  color: #15a34a;
}
.doc-icon-image {
  color: #0d6efd;
}
.image-preview {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}
.folder-actions {
  display: inline-flex;
  align-items: center;
}
.folder-action {
  font-size: 0.85rem;
  cursor: pointer;
  opacity: 0.7;
}
.folder-action:hover {
  opacity: 1;
}
</style>
